import React from "react";

import { AutoBind } from "../../../rule-engine-core/Common/AutoBind";
import { Sidebar, SidebarItem } from "../../../CommonUI/BuilderUI/Sidebar";
import { Topbar } from "../../../CommonUI/BuilderUI/Topbar";
import { DataIcon } from "../../../CommonUI/Icon/DataIcon";
import { RuleTableIcon } from "../../../CommonUI/Icon/RuleTableIcon";

import { ObserveState } from "../../../CommonUI/StateManagement/ObserveState";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { RuleEditorDiv } from "../../DesignUI/CommonStyles";
import { RuleBuilderPage } from "../../../rule-engine-core/Rule/RuleBuilderPage";
import { RuleTableDesigner } from "./Builder/RuleTableDesigner";
import { RuleTableEditContext } from "../../../rule-engine-core/Rule/Table/Rule/RuleTableEditContext";
import { RuleTableConfiguration } from "../../../rule-engine-core/Rule/Table/Rule/RuleTableConfiguration";
import { DataDefinition } from "../../../rule-engine-core/Common/Data/DataDefinition";

import { XExecutableTester } from "../../../CommonUI/Testing/XExecutableTester";
import { TestDataIcon } from "../../../CommonUI/Icon/TestDataIcon";
import { BuilderEditPage } from "../../../CommonUI/BuilderUI/BuilderEditPage";
import { CodingIcon } from "../../../CommonUI/Icon/CodingIcon";
import { DataBuilder } from "../../../DataBuilder/DataBuilder";
import { UnitTestConfiguration } from "../../../rule-engine-core/Common/UnitTesting/UnitTestConfiguration";

export interface IXRuleTableEditor {
    ruleDefinition: RuleTableConfiguration;
    dataDefinition?: DataDefinition;
    ruleTableName?: string;
    onRuleTableNameUpdate?: (newName: string) => void;
    onRuleSave?: () => void;
    onRuleCancel?: () => void;
    saveUnitTest?: (unitTest: UnitTestConfiguration) => void;
    getUnitTests?: () => Promise<UnitTestConfiguration[]>;
}

export class RuleTableBuilder extends React.Component<IXRuleTableEditor, {}> {
    editContext: RuleTableEditContext;
    tabs: SidebarItem[];
    ruleDefinition: RuleTableConfiguration;

    constructor(props: IXRuleTableEditor) {
        super(props);

        this.ruleDefinition = props.ruleDefinition;
        this.editContext = new RuleTableEditContext(this.ruleDefinition);

        if (props.ruleTableName) {
            this.editContext.title = props.ruleTableName;
        }

        if (props.onRuleSave) {
            this.editContext.onRuleSave = props.onRuleSave;
        }

        if (props.onRuleCancel) {
            this.editContext.onRuleCancel = props.onRuleCancel;
        }

        if (props.dataDefinition) {
            props.ruleDefinition.inputData = props.dataDefinition;
        }

        this.tabs = this.getDefaultTabs();
    }

    @AutoBind
    onChangeRuleName(newName: string) {
        this.editContext.title = newName;
        StateManager.propertyChanged(this.editContext, "title");
    }

    getDefaultTabs(): SidebarItem[] {
        return [
            {
                value: RuleBuilderPage.Rule,
                renderUI: () => (
                    <RuleTableDesigner
                        editContext={this.editContext}
                        ruleDefinition={this.ruleDefinition}
                    />
                ),
                icon: <RuleTableIcon />,
            },
            {
                value: RuleBuilderPage.Data,
                renderUI: () => (
                    <DataBuilder
                        definition={this.props.ruleDefinition.getInputDefinition()}
                        readOnly={!this.props.ruleDefinition.canChangeInputs}
                    />
                ),
                icon: <DataIcon />,
            },
            {
                value: RuleBuilderPage.Configuration,
                renderUI: () => (
                    <BuilderEditPage
                        builder={this.props.ruleDefinition}
                        updateCallback={() => { }}
                    />
                ),
                icon: <CodingIcon />,
            },
            {
                value: RuleBuilderPage.Test,
                renderUI: () => (
                    <XExecutableTester
                        saveUnitTest={this.props.saveUnitTest}
                        executable={this.props.ruleDefinition}
                    />
                ),
                icon: <TestDataIcon />,
            },
        ];
    }

    render() {
        return (
            <RuleEditorDiv>
                <ObserveState
                    listenTo={this.editContext}
                    properties={["title"]}
                    control={() => {
                        return (
                            <Topbar
                                name={this.editContext.title}
                                onChangeName={this.onChangeRuleName}
                                editContext={this.editContext}
                                isActionDisabled={!this.editContext.lastSelectionElement}
                                isRequiredActionGroup={false}
                                onSave={
                                    this.editContext.onRuleSave
                                        ? () => {
                                            if (this.editContext.onRuleSave) {
                                                this.editContext.onRuleSave();
                                            }
                                        }
                                        : undefined
                                }
                                onClose={this.editContext.onRuleCancel}
                            />
                        );
                    }}
                />
                <ObserveState
                    listenTo={this.editContext}
                    properties={["mode"]}
                    control={() => (
                        <Sidebar
                            items={this.tabs}
                            value={this.editContext.mode}
                            onChange={(mode: string) => {
                                Reflect.set(this.editContext, "mode", mode);
                                StateManager.propertyChanged(this.editContext, "mode");
                            }}
                        />
                    )}
                />
            </RuleEditorDiv>
        );
    }
}
